import clsx from 'clsx';
import {useEffect, useMemo, useState} from 'react';

import AnimatedMediaBackground from '@/components/AnimatedMediaBackground/AnimatedMediaBackground';
import BackButton from '@/components/BackButton/BackButton';
import Media from '@/components/Media/Media';
import type {TipsInstruction, TipsItem} from '@/services/content/tipsContent.types';
import eventMessagesService from '@/services/events/eventMessagesService';
import type {ImageModel, VideoModel} from '@/types/content';

import DetailView from './DetailView';

type PopupPageViewProps = {
    card: TipsItem;
    onBack: () => void;
    isEmbedded: boolean;
};

const PopupPageView = ({card, onBack, isEmbedded}: PopupPageViewProps) => {
    const sortedInstructions = useMemo(
        () => [...(card.instructions ?? [])].sort((a, b) => a.order - b.order),
        [card.instructions]
    );
    const firstInstruction = sortedInstructions[0] ?? null;
    const [currentInstruction, setCurrentInstruction] = useState<TipsInstruction | null>(firstInstruction);
    const [maxInstructionHeight, setMaxInstructionHeight] = useState(0);

    useEffect(() => {
        if (card.guideItemType) eventMessagesService.sendTipOpened(card.guideItemType);
    }, [card.guideItemType]);

    const video = currentInstruction?.video as VideoModel | undefined;
    const images = (currentInstruction?.images ?? card.images) as ImageModel[] | undefined;
    const isImageOnlyPopup = !video && Boolean(images?.length);

    return (
        <div
            className={clsx(
                'relative flex h-full min-h-0 w-full flex-col text-white',
                isImageOnlyPopup ? 'overflow-hidden' : 'overflow-x-hidden',
                isEmbedded ? 'bg-tips-surface-primary' : 'bg-black'
            )}
        >
            {!isImageOnlyPopup && (
                <AnimatedMediaBackground
                    isPopupOpen
                    isStatic
                    mainImage={images}
                    video={video}
                    backgroundClassName={isEmbedded ? 'bg-tips-surface-primary' : undefined}
                />
            )}

            <BackButton
                onClick={onBack}
                className={clsx(
                    'absolute z-50',
                    isEmbedded ? 'top-8 left-8 bg-tips-back-button-surface' : 'top-safe-offset-4 left-4'
                )}
            />

            {isImageOnlyPopup ? (
                <div className='relative flex min-h-0 w-full flex-1 flex-col overflow-hidden'>
                    <div
                        className={clsx(
                            'pointer-events-none flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden',
                            isEmbedded ? 'pt-20' : 'pt-safe-offset-14'
                        )}
                    >
                        {images && images.length > 0 && (
                            <Media
                                images={images}
                                className='flex h-full max-h-full w-full items-center justify-center'
                                imgContainerClassName='flex h-full max-h-full w-full items-center justify-center'
                                pictureClassName='flex h-full max-h-full w-full items-center justify-center'
                                imgClassName='max-h-full max-w-full object-contain'
                            />
                        )}
                    </div>
                    <div
                        className='w-full shrink-0'
                        style={maxInstructionHeight > 0 ? {height: `${maxInstructionHeight}px`} : undefined}
                        aria-hidden='true'
                    />

                    <div className='absolute inset-0 z-10 min-h-0 overflow-hidden'>
                        {sortedInstructions.length > 0 && (
                            <DetailView
                                instructions={sortedInstructions}
                                guideItemType={card.guideItemType}
                                onInstructionChange={setCurrentInstruction}
                                onMaxInstructionHeightChange={setMaxInstructionHeight}
                                splitImageLayout
                            />
                        )}
                    </div>
                </div>
            ) : (
                <div className='relative z-10 flex min-h-0 w-full flex-1 flex-col justify-end'>
                    {sortedInstructions.length > 0 && (
                        <DetailView
                            instructions={sortedInstructions}
                            guideItemType={card.guideItemType}
                            onInstructionChange={setCurrentInstruction}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default PopupPageView;
